import { createURL } from '@sm/utils';
import { t, defineMessages } from '@sm/intl';
import {
  hasCX,
  hasEngage,
  hasTechValidate,
  hasWufoo,
  hasApply,
  getWufooURL,
  hasUsabilla,
  showBlog,
  usabillaLink,
  hasGetFeedback,
} from '../../../../helpers/languageHelpers.js';
import { getHelpCenterLink } from '../../../../helpers/getHelpLink';

const COPY = defineMessages({
  ENTERPRISE: {
    id: 'getDrawerData.enterprise',
    defaultMessage: 'Enterprise',
    description:
      '[Type: Label][Vis.: high] - This is the Enterprise section of the side menu.',
  },
  ENTERPRISE_SUBTITLE: {
    id: 'getDrawerData.enterprise_subtitle',
    defaultMessage: 'Get more security & control over your survey data',
    description:
      '[Type: Label][Vis.: high] - This is the Enterprise subtitle section of the side menu.',
  },
  AUDIENCE: {
    id: 'getDrawerData.audience',
    defaultMessage: 'Audience',
    description: '[Type: Label][Vis.: low] - Audience section of sidemenu',
  },
  AUDIENCE_SUBTITLE: {
    id: 'getDrawerData.audienceSubtitle',
    defaultMessage: 'Collect survey responses from our global consumer panel',
    description: '[Type: Label][Vis.: low] - Audience subtitle of sidemenu',
  },
  INTEGRATIONS_AND_PLUGINS: {
    id: 'getDrawerData.integrationsAndPlugins',
    defaultMessage: 'Integrations & Plug-ins',
    description: '[Type: Label][Vis.: low] - integrations label of submenu',
  },
  INTEGRATIONS_AND_PLUGINS_SUBTITLE: {
    id: 'getDrawerData.integrationsAndPluginsSubtitle',
    defaultMessage: 'Easily connect survey data to existing business systems',
    description:
      '[Type: Label][Vis.: low] - integrations subtitle section of submenu',
  },
  WUFOO_SUBTITLE: {
    id: 'getDrawerData.wufooSubtitle',
    defaultMessage: 'Gather data & payments with online forms',
    description: '[Type: Label][Vis.: low] - wufoo subtitle section of submenu',
  },
  APPLY_SUBTITLE: {
    id: 'getDrawerData.applySubtitle',
    defaultMessage: 'Collect, review & manage applications online',
    description: '[Type: Label][Vis.: low] - apply subtitle section of submenu',
  },
  USABILLA_SUBTITLE: {
    id: 'getDrawerData.usabillaSubtitle',
    defaultMessage: 'Get in-the-moment feedback across all digital channels',
    description:
      '[Type: Label][Vis.: low] - Usabilla subtitle section of submenu',
  },
  PRODUCTS_FOOTNOTE: {
    id: 'getDrawerData.productsFootnote',
    defaultMessage:
      'Net Promoter Score and NPS are registered trademarks of Bain & Company, Inc., Fred Reichheld and Satmetrix Systems, Inc.',
    description:
      '[Type: Label][Vis.: low] - products footnote section of submenu',
  },
  HELP_CENTER: {
    id: 'getDrawerData.helpCenter',
    defaultMessage: 'Help Center',
    description: '[Type: Label][Vis.: low] - Help Center section of submenu',
  },
  HELP_CENTER_SUBTITLE: {
    id: 'getDrawerData.helpCenterSubtitle',
    defaultMessage: 'Find quick answers to your questions',
    description:
      '[Type: Label][Vis.: low] - Help Center subtitle section of submenu',
  },
  SURVEYMONKEY_BLOG: {
    id: 'getDrawerData.SurveyMonkeyBlog',
    defaultMessage: 'Curiosity at Work',
    description: '[Type: Label][Vis.: low] - SurveyMonkey section of submenu',
  },
  SURVEYMONKEY_BLOG_SUBTITLE: {
    id: 'getDrawerData.SurveyMonkeyBlogSubtitle',
    defaultMessage: 'Get inspiration on our blog',
    description:
      '[Type: Label][Vis.: low] - SurveyMonkey blog subtitle of submenu',
  },
});

export const createURLHelper = (product, program, family, interest, code) => {
  const date = new Date();
  const ISODate = date.toISOString().slice(0, 10);
  const utSourceIntegrations = 'header_products_launchpad_integrations_apps';
  const url = {
    ut_source: `header_products_launchpad_${product}` || utSourceIntegrations,
    utm_source: 'surveymonkey',
    utm_system: 'product',
    utm_medium: 'product',
    utm_content: `${product}-site`,
    utm_term: product,
    utm_campaign: `header_products_launchpad_${product}`,
    adid: '',
    family: family.charAt(0).toUpperCase() + family.substring(1),
    date: ISODate,
    program: `7013A000000mY${program}`,
    interest,
    code,
    source: 'SurveyMonkey',
    recent: 'SurveyMonkey',
  };
  return url;
};

export const getProductsItems = (lang, isEUDC) => {
  const items = [
    {
      title: t(COPY.ENTERPRISE),
      subTitle: t(COPY.ENTERPRISE_SUBTITLE),
      url: createURL(
        '/mp/enterprise/',
        createURLHelper('enterprise', '7MQAU', 'Enterprise', 'SME', 'SME')
      ),
      navItem: 'enterprise',
    },
    !isEUDC && {
      title: t(COPY.AUDIENCE),
      subTitle: t(COPY.AUDIENCE_SUBTITLE),
      url: createURL('/market-research/solutions/audience-panel/', {
        ut_source: 'header_products_launchpad_audience',
      }),
      navItem: 'audience',
    },
    {
      title: t(COPY.INTEGRATIONS_AND_PLUGINS),
      subTitle: t(COPY.INTEGRATIONS_AND_PLUGINS_SUBTITLE),
      url: createURL(
        '/apps/',
        createURLHelper(
          'integrations',
          '7RQAU',
          'Enterprise',
          'Salesforce',
          'SME'
        )
      ),
      navItem: 'integrations',
    },
    hasCX(lang) && {
      title: 'CX',
      subTitle: 'Understand & improve customer experience (NPS®)',
      url: createURL(
        'https://www.surveymonkey.com/cx/',
        createURLHelper('cx', '7bQAE', 'Solutions', 'CX', 'CX')
      ),
      navItem: 'cx',
    },
    hasEngage(lang) && {
      title: 'Engage',
      subTitle: 'Understand & increase employee engagement',
      url: createURL(
        'https://www.surveymonkey.com/engage/',
        createURLHelper('engage', '7vQAE', 'Solutions', 'Engage', 'Engage')
      ),
      navItem: 'engage',
    },
    hasTechValidate(lang) && {
      title: 'TechValidate',
      subTitle: 'Create marketing content from customer feedback',
      url: createURL(
        'https://www.surveymonkey.com/techvalidate/',
        createURLHelper('techvalidate', '880QAE', 'Solutions', 'TV', 'TV')
      ),
      navItem: 'tech validate',
    },
    hasWufoo(lang) && {
      title: 'Wufoo',
      subTitle: t(COPY.WUFOO_SUBTITLE),
      url: createURL(getWufooURL(lang), {
        ut_source: 'header_products_launchpad_wufoo',
      }),
      navItem: 'wufoo',
    },
    hasApply(lang) && {
      title: 'Apply',
      subTitle: t(COPY.APPLY_SUBTITLE),
      url: createURL('https://apply.surveymonkey.com/', {
        ut_source: 'header_products_launchpad_apply',
        program: '7013A000000mY8AQAU',
      }),
      navItem: 'apply',
    },
    hasUsabilla(lang) && {
      title: 'Usabilla',
      subTitle: t(COPY.USABILLA_SUBTITLE),
      url: createURL(usabillaLink(lang), {
        utm_source: 'header_products_launchpad',
      }),
      navItem: 'usabilla',
    },
    hasGetFeedback(lang) && {
      title: 'GetFeeback',
      subTitle: 'Customer feedback for Salesforce',
      url: createURL('https://www.getfeedback.com/', {
        utm_source: 'surveymonkey',
      }),
      openInNewWindow: true,
      navItem: 'getfeedback',
    },
  ];
  return items.filter(Boolean);
};

export const getProductsfootnote = () => {
  return t(COPY.PRODUCTS_FOOTNOTE);
};

export const getResourcesItems = lang => {
  const items = [
    {
      title: t(COPY.HELP_CENTER),
      subTitle: t(COPY.HELP_CENTER_SUBTITLE),
      url: getHelpCenterLink(lang, {
        params: {
          ut_source: 'product',
          ut_source3: 'header',
        },
      }),
      navItem: 'help center',
    },
    showBlog(lang) && {
      title: t(COPY.SURVEYMONKEY_BLOG),
      subTitle: t(COPY.SURVEYMONKEY_BLOG_SUBTITLE),
      url: createURL('/curiosity/', { ut_source: 'header' }),
      navItem: 'resource center',
    },
  ];
  return items.filter(Boolean);
};
